import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The Cards view of a people directory (prototype `personCards`): divided cells in the table's white
 * container, not cards on a card. Put `PersonCard`s inside.
 */
export const PersonCardGrid = ({ children }: { children: ReactNode }) => (
  <div className="overflow-hidden">
    <div className="-mb-px -mr-px grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))]">
      {children}
    </div>
  </div>
);

interface PersonCardProps {
  /** Accessible name of the card, which opens the person. */
  label: string;
  avatar: ReactNode;
  /** The row ⋮ menu, top right. */
  menu?: ReactNode;
  name: string;
  /** Designation or role under the name. */
  role?: string;
  /** A small line under the role (prototype `meta`: department · ID). */
  meta?: ReactNode;
  /** Skill chips. */
  chips?: ReactNode;
  /** Today's presence, pinned to the foot of the card. */
  status?: ReactNode;
  onOpen: () => void;
}

/** One person of the Cards view (prototype `pcard`). */
export const PersonCard = ({
  label,
  avatar,
  menu,
  name,
  role,
  meta,
  chips,
  status,
  onOpen,
}: PersonCardProps) => {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };
  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={label}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      className={cn(
        'flex h-full cursor-pointer flex-col gap-1 border-b border-r p-4 outline-none',
        'transition-colors duration-fast ease-standard hover:bg-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
      )}
    >
      <div className="mb-2 flex items-start justify-between">
        {avatar}
        {menu}
      </div>
      <b className="font-semibold">{name}</b>
      {role && <span className="text-[13px] text-muted-foreground">{role}</span>}
      {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
      {chips && <div className="mt-2 mb-3">{chips}</div>}
      {status && <div className="mt-auto border-t pt-2.5 text-xs">{status}</div>}
    </div>
  );
};
